import React, { useId, useLayoutEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { focusables } from './focusable';
import './Dialog.css';

export interface DialogProps {
    open: boolean;
    /** Called for Escape, the close button, and (when enabled) a backdrop click. */
    onClose: () => void;
    /** Visible heading; also the dialog's accessible name. */
    title: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    /** Close when the backdrop is clicked. Turn off for forms holding unsaved input. */
    closeOnBackdrop?: boolean;
    className?: string;
}

/**
 * Modal dialog on native <dialog>.showModal(): the rest of the page is inert,
 * Tab is kept inside, Escape closes, focus returns to the opener on close.
 * Page scroll is locked by Dialog.css while any .ui-dialog is open.
 */
export function Dialog({ open, ...props }: DialogProps) {
    return open ? <ModalDialog {...props} /> : null;
}

export interface DrawerProps extends DialogProps {
    side?: 'left' | 'right';
}

/** Same behaviour as Dialog, docked to a screen edge at full height. */
export function Drawer({ open, side = 'right', className = '', ...props }: DrawerProps) {
    return open ? <ModalDialog {...props} className={`ui-dialog--drawer ui-dialog--${side} ${className}`} /> : null;
}

function ModalDialog({ onClose, title, children, footer, closeOnBackdrop = true, className = '' }: Omit<DialogProps, 'open'>) {
    const ref = useRef<HTMLDialogElement>(null);
    const titleId = useId();

    useLayoutEffect(() => {
        const dialog = ref.current;
        if (!dialog) return;
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        dialog.showModal();
        const initial = dialog.querySelector<HTMLElement>('[autofocus]')
            ?? focusables(dialog.querySelector('.ui-dialog-body'))[0]
            ?? focusables(dialog)[0]
            ?? dialog;
        initial.focus();
        return () => {
            if (dialog.open) dialog.close();
            if (opener?.isConnected) opener.focus();
        };
    }, []);

    // Set on pointerdown so a text-selection drag that ends on the backdrop is not a backdrop click.
    const downOnBackdrop = useRef(false);

    const onKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
        if (e.key !== 'Escape' && e.key !== 'Tab') return;
        // React events bubble through the component tree, so a nested dialog would
        // otherwise also close or trap its parent.
        e.stopPropagation();
        if (e.key === 'Escape') {
            e.preventDefault(); // stops the native cancel so onClose stays the single close path
            onClose();
            return;
        }
        const els = focusables(e.currentTarget);
        if (els.length === 0) { e.preventDefault(); return; }
        const first = els[0];
        const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    return (
        <dialog
            ref={ref}
            className={`ui-dialog ${className}`}
            aria-labelledby={titleId}
            onKeyDown={onKeyDown}
            // Close requests that bypass keydown (e.g. Android back).
            onCancel={(e) => { e.preventDefault(); onClose(); }}
            // Some close requests are not cancelable and close the element directly;
            // report them so the owner's open state stays in sync. The event is async:
            // one queued by our own effect cleanup can land after a StrictMode remount
            // has reopened the dialog, so only a dialog that is still closed counts.
            onClose={(e) => { if (e.target === e.currentTarget && !e.currentTarget.open) onClose(); }}
            // The panel fills the dialog, so only a ::backdrop press targets the dialog itself.
            onPointerDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
            onClick={(e) => {
                const backdrop = downOnBackdrop.current && e.target === e.currentTarget;
                downOnBackdrop.current = false;
                if (closeOnBackdrop && backdrop) onClose();
            }}
        >
            <div className="ui-dialog-panel">
                <header className="ui-dialog-header">
                    <h2 id={titleId} className="ui-dialog-title">{title}</h2>
                    <button type="button" className="btn btn-ghost ui-dialog-close" aria-label="Close" onClick={onClose}>
                        <X size={18} aria-hidden="true" />
                    </button>
                </header>
                <div className="ui-dialog-body">{children}</div>
                {footer && <footer className="ui-dialog-footer">{footer}</footer>}
            </div>
        </dialog>
    );
}
